import type { LucideIcon } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { colorHex } from '../lib/reporteColor'
import { formatNumero } from '../lib/paisMapa'

export interface SubStat {
  label: string
  value: string | number
  /** Color semántico del valor (Tailwind class). Default: text-muted-foreground. */
  tone?: 'default' | 'success' | 'warning' | 'danger' | 'muted'
}

interface Props {
  icon: LucideIcon
  title: string
  /** Valor principal grande a la izquierda. */
  value: string | number | null | undefined
  /** Texto chico bajo el valor principal (ej. "Totales históricos"). */
  hint?: string
  /** Sub-stats que aparecen alineados a la derecha. */
  subStats?: SubStat[]
  colorNombre: string
  colorTono?: string
  isLoading?: boolean
  /** Id de configuración para el formateo de números según el país del reporte. */
  configId?: number
}

const TONE_CLASSES: Record<NonNullable<SubStat['tone']>, string> = {
  default: 'text-foreground',
  success: 'text-emerald-600 dark:text-emerald-400',
  warning: 'text-amber-700 dark:text-amber-500',
  danger: 'text-red-500 dark:text-red-400',
  muted: 'text-muted-foreground',
}

function formatValue(v: string | number | null | undefined, configId: number): string {
  if (v === null || v === undefined) return '—'
  if (typeof v === 'number') return formatNumero(v, configId)
  return v
}

export function StatCardConsolidado({
  icon: Icon,
  title,
  value,
  hint,
  subStats,
  colorNombre,
  colorTono = '500',
  isLoading,
  configId,
}: Props) {
  const hex = colorHex(colorNombre, colorTono)
  // Mismo gradient sutil del DashboardStats (medifarma)
  const cardStyle = {
    background: `linear-gradient(to right, ${hex}26 0%, ${hex}0d 50%, transparent 100%)`,
  }

  return (
    <Card className="border border-gray-300 dark:border-gray-700" style={cardStyle}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="h-4 w-4" style={{ color: hex }} />
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-2xl font-bold text-black dark:text-white">
              {isLoading ? <Skeleton className="h-8 w-20" /> : formatValue(value, configId ?? 0)}
            </div>
            {hint && !isLoading && (
              <p className="text-[10px] text-muted-foreground">{hint}</p>
            )}
          </div>
          {subStats && subStats.length > 0 && (
            <div className="flex flex-col items-end gap-0.5 text-xs text-muted-foreground">
              {isLoading
                ? Array.from({ length: subStats.length }).map((_, i) => (
                    <Skeleton key={i} className="h-3 w-24" />
                  ))
                : subStats.map((s, i) => (
                    <span key={i} className={TONE_CLASSES[s.tone ?? 'muted']}>
                      {s.label}: {formatValue(s.value, configId ?? 0)}
                    </span>
                  ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
